import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCaretLeft, faThumbtack, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { makeLookupTreeFromItems, textHasMatch, resolveAllImplicitLinks } from '../biz/linkFinder';
import { LookupTree, SquiggleItem, SquiggleTypes } from '../types/types';
import './LinkItem.css';
import ItemSelector from '../shared/ItemSelector';
import { useAdventureDataContext } from '../context/AdventureDataContext';
import { unstable_batchedUpdates } from 'react-dom';
import { isSystemItem, systemItems } from '../biz/systemItems';

interface FoundLink {
  id: string;
  name: string;
  type: SquiggleTypes;
}

interface ImplicitLinks {
  intrinsic: FoundLink[];
  extrinsic: FoundLink[];
}

const findLinks = (item: SquiggleItem, itemLookupTree: LookupTree, allItems: SquiggleItem[], includeSystem: boolean): ImplicitLinks => {
  const foundLinks = resolveAllImplicitLinks(itemLookupTree, item.text, item.id);
  const intrinsic: FoundLink[] = foundLinks
    .filter(l => includeSystem || !isSystemItem(l.id))
    .map(l => ({ id: l.id, name: l.name, type: allItems.find(i => i.id === l.id)!.type }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const extrinsic: FoundLink[] = [];
  const thisLookupTree = makeLookupTreeFromItems([item]);

  for (let otherItem of allItems) {
    if (item.id !== otherItem.id) {
      if (textHasMatch(thisLookupTree, otherItem.text)) {
        extrinsic.push({ id: otherItem.id, name: otherItem.name, type: otherItem.type });
      }
    }
  }
  extrinsic.sort((a, b) => a.name.localeCompare(b.name));
  return {intrinsic, extrinsic};
};

function LinkItem({
  item,
  isSelected,
  link,
  addLink,
  removeLink,
  setParent,
  back
} : {
  item: SquiggleItem,
  isSelected: boolean,
  link: (itemId: string, selectItem: boolean) => void,
  addLink: (itemId: string, otherId: string) => void,
  removeLink: (itemId: string, otherId: string) => void,
  setParent: (itemId: string, parentId: string) => void,
  back: () => void
}) {
  const { allUserItems, itemById, itemLookupTree } = useAdventureDataContext();

  const implicitLinks = useMemo(() => {
    return item.type !== 'system' ? findLinks(item, itemLookupTree, allUserItems, false) : findLinks(item, itemLookupTree, systemItems, true);
  }, [item, itemLookupTree, allUserItems]);
  const [showExplicitLinkRow, setShowExplicitLinkRow] = useState<boolean>(false);

  const availableItems = useMemo(() =>
    allUserItems.filter(i => i.id !== item.id && !i.archived && !item.links.includes(i.id)),
    [allUserItems, item]
  );

  const availableParents = useMemo(() =>
    allUserItems.filter(i => i.id !== item.id && i.type === item.type && i.parent !== item.id),
    [allUserItems, item]
  );

  const children = useMemo(() =>
    allUserItems.filter(i => i.parent === item.id),
    [allUserItems, item]
  );

  const explicitLinks = useMemo(() =>
    item.links.filter(linkId => item.parent !== linkId && !children.find(c => c.id === linkId))
      .map(linkId => itemById.get(linkId))
      .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
  , [itemById, item, children]);
  const intrinsicLinks = implicitLinks.intrinsic.filter(linkItem => item.parent !== linkItem.id && !children.find(c => c.id === linkItem.id) && !item.links.includes(linkItem.id));
  const extrinsicLinks = implicitLinks.extrinsic.filter(linkItem => item.parent !== linkItem.id && !children.find(c => c.id === linkItem.id) && !item.links.includes(linkItem.id));

  const tryLinking = useCallback((e: MouseEvent<HTMLLIElement>) => {
    if (e.target && e.currentTarget.dataset.id) {
      link(e.currentTarget.dataset.id, !e.shiftKey);
      e.stopPropagation();
    }
  }, [link]);

  const tryLinkingParent = useCallback((e: MouseEvent<HTMLLabelElement>) => {
    if (item.parent) {
      link(item.parent, !e.shiftKey);
      e.stopPropagation();
    }
  }, [link, item]);

  const openEverything = useCallback(() => {
    for (let child of children) {
      link(child.id, false);
    }
    if (item.parent) {
      link(item.parent, false);
    }
    for (let itemId of item.links) {
      link(itemId, false);
    }
    for (let itemLink of implicitLinks.intrinsic) {
      link(itemLink.id, false);
    }
    for (let itemLink of implicitLinks.extrinsic) {
      link(itemLink.id, false);
    }
  }, [implicitLinks, link, item, children]);

  const openAllChildren = useCallback(() => {
    for (let child of children) {
      link(child.id, false);
    }
  }, [link, children]);

  const openAllExplicit = useCallback(() => {
    for (let item of explicitLinks) {
      if (item) {
        link(item.id, false);
      }
    }
  }, [link, explicitLinks]);

  const openAllIntrinsic = useCallback(() => {
    for (let item of intrinsicLinks) {
      link(item.id, false);
    }
  }, [link, intrinsicLinks]);

  const openAllExtrinsic = useCallback(() => {
    for (let item of extrinsicLinks) {
      link(item.id, false);
    }
  }, [link, extrinsicLinks]);

  const addLinkCallback = useCallback((itemId: string) => {
    addLink(item.id, itemId);
  }, [addLink, item]);

  useEffect(() => {
    if (!isSelected) {
      return;
    }

    const listener = (e: KeyboardEvent) => {
      if ((e.key === 'o' || e.key === 'O') && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        unstable_batchedUpdates(() => {
          openEverything();
        });
        return;
      }

      if (e.key === 'Escape' && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        back();
        return;
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [isSelected, back, openEverything]);

  return <>
    <div className="formrow linknamerow"><span className="name">{item.name}</span></div>
    <div className="links">
      { item.type === "system" ?
        (<>
          <div className="formrow">
            <label className="parentLabel" onClick={tryLinkingParent}>Parent:</label>
            <ItemSelector className='linkselect' availableItems={availableParents} selectedItemId={""} noneText="None" emptyParentName="" onChange={() => {}} />              
          </div>
          <div className="formrow">
            <div className="linkheading sysmsg" style={{ paddingTop: '0.5em' }}>System information links are read-only.</div>
          </div>
        </>) :
        (<div className="formrow">
          <label className="parentLabel" onClick={tryLinkingParent}>Parent:</label>
          <ItemSelector className='linkselect' availableItems={availableParents} selectedItemId={item.parent || ""} onChange={(parentId) => setParent(item.id, parentId)} noneText="None" emptyParentName="" />
        </div>)
      }
      {
        children.length > 0 &&
        <>
          <div className="formrow"><div className="linkcategory">Children</div><div className="linkactions"><a onClick={openAllChildren}>Open all</a></div></div>
          <div className="formrow">
            <ul>
              { children.map(childItem =>
                  <li key={childItem.id} className={childItem.type} onClick={tryLinking} data-id={childItem.id}>
                    {childItem.name}
                  </li>
                )
              }
            </ul>
          </div>
        </>
      }
      {
        explicitLinks.length > 0 &&
        <>
          <div className="formrow"><div className="linkcategory">Explicit links</div><div className="linkactions"><a onClick={openAllExplicit}>Open all</a></div></div>
          <div className="formrow">
            <ul>
              { explicitLinks.map(linkItem =>
                  linkItem && <li key={linkItem.id} className={linkItem.type} onClick={tryLinking} data-id={linkItem.id}>
                    { item.type !== 'system' && <span className="removeLink" onClick={(e) => { removeLink(item.id, linkItem.id); e.stopPropagation(); }}><FontAwesomeIcon icon={faXmark as IconProp} /></span> }
                    {linkItem.name}
                  </li>
                )
              }
            </ul>
          </div>
        </>
      }
      {
        intrinsicLinks.length > 0 &&
        <>
          <div className="formrow"><div className="linkcategory">Mentioned items</div><div className="linkactions"><a onClick={openAllIntrinsic}>Open all</a></div></div>
          <div className="formrow">
            <ul>
              { intrinsicLinks.map(linkItem =>
                  <li key={linkItem.id} className={linkItem.type} onClick={tryLinking} data-id={linkItem.id}>
                    { item.type !== 'system' && <span className="pinLink" onClick={(e) => { addLink(item.id, linkItem.id); e.stopPropagation(); }}><FontAwesomeIcon icon={faThumbtack as IconProp} /></span> }
                    {linkItem.name}
                  </li>
                )
              }
            </ul>
          </div>
        </>
      }
      {
      extrinsicLinks.length > 0 &&
      <>
        <div className="formrow"><div className="linkcategory">Mentions of this item in others</div><div className="linkactions"><a onClick={openAllExtrinsic}>Open all</a></div></div>
        <div className="formrow">
          <ul>
            { extrinsicLinks.map(linkItem =>
                <li key={linkItem.id} className={linkItem.type} onClick={tryLinking} data-id={linkItem.id}>
                  { item.type !== 'system' && <span className="pinLink" onClick={(e) => { addLink(item.id, linkItem.id); e.stopPropagation(); }}><FontAwesomeIcon icon={faThumbtack as IconProp} /></span> }
                  {linkItem.name}
                </li>
              )
            }
          </ul>
        </div>
        </>
      }
    </div>
    <div className="formrow grow"></div>
    {
      showExplicitLinkRow && <div className="formrow">
        <label>Link to:</label>
        <ItemSelector className="linkselect" availableItems={availableItems} selectedItemId="" onChange={addLinkCallback} hiddenNone={true} emptyParentName="Unavailable parent" />
      </div>
    }
    <div className="buttonrow reverse">
      <div className="buttoncontainer">
        { !showExplicitLinkRow && <button className={item.type === 'system' ? 'disabled' : ''} onClick={() => item.type !== 'system' && setShowExplicitLinkRow(true)}>Add links</button> }
        <button onClick={openEverything}><span className="hotkey">O</span>pen everything</button>
      </div>
      <div className="buttoncontainer grow">
        <button title="Back [Esc]" onClick={back}><FontAwesomeIcon icon={faCaretLeft as IconProp}></FontAwesomeIcon>Back</button>
      </div>
    </div>
  </>;
}

export default LinkItem;